import Button from "@/components/common/Button";
import Card from "@/components/common/Card";
import PageHeader from "@/components/common/PageHeader";
import StatCard from "@/components/dashboard/StatCard";
import { useCourses } from "@/hooks/queries/useCourseQueries";
import { useUserRanking } from "@/hooks/queries/useUserQueries";
import { useRequestCourseAssignment } from "@/hooks/queries/useCourseAssignmentMutations";
import { useAuth } from "@/hooks/useAuth";
import {
  AcademicCapIcon,
  CheckBadgeIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import "@/lib/chartConfig";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDate, formatPercentage, formatScore, formatProgressBar } from "@/utils/formatters";
import toast from "react-hot-toast";

// Define a type for course objects
interface DashboardCourse {
  _id: string;
  title: { [key: string]: string };
  // Add other properties as needed
}




const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const getCourseTitle = (course: DashboardCourse) => {
    if (language === "am") return course.title?.am || course.title?.en || "";
    if (language === "en") return course.title?.en || course.title?.am || "";
    // Default to English for other languages
    return course.title?.en || course.title?.am || "";
  };

  const { user } = useAuth();
  // Removed old assigned courses logic - now using same data source as courses page

  const { data: courses = [], isLoading } = useCourses();
  const { data: rankingData, isLoading: rankingLoading } = useUserRanking();
  const requestCourseAssignmentMutation = useRequestCourseAssignment();
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [requestSent, setRequestSent] = useState(false);

  // Debug ranking data
  console.log("🔍 Ranking Debug:", {
    rankingData,
    rankingLoading,
    userRole: user?.role,
    shouldShowRanking:
      user?.role === "employee" || user?.role === "department_admin",
  });

  // Set default selected course to first available course
  useEffect(() => {
    if (courses && courses.length > 0 && !selectedCourse) {
      setSelectedCourse(courses[0]._id);
    }
  }, [courses, selectedCourse]);

  // Calculate dashboard stats using the same data source as courses page
  const learningProgress = (user as any)?.learningProgress || [];
  const completedCourses = learningProgress.filter(
    (c: any) => c.progress === 100 || c.completedAt
  ).length;
  const assignedCoursesCount = courses.length; // Use total courses count like courses page
  const overallProgress =
    learningProgress.length > 0
      ? Math.round(
          learningProgress.reduce(
            (sum: number, c: any) => sum + (c.progress || 0),
            0
          ) / learningProgress.length
        )
      : 0;

  // Handle course assignment request
  const handleRequestCourseAssignment = async () => {
    if (!user?._id) return;

    try {
      await requestCourseAssignmentMutation.mutateAsync(user._id);
      setRequestSent(true);
      toast.success(t("dashboard.requestSent", "Request sent successfully!"));
    } catch (error: any) {
      console.error("Failed to send course assignment request:", error);
      toast.error(
        error?.response?.data?.message ||
          t(
            "dashboard.requestFailed",
            "Failed to send request. Please try again."
          )
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }
  return (
    <div>
      <PageHeader
        title={t("dashboard.welcome", { name: user?.fullName })}
        description={t("dashboard.welcomeDescription")}
      />
      {/* Stats overview */}
      <div className="mt-4 sm:mt-6 grid gap-4 sm:gap-6 grid-cols-2 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title={t("dashboard.assignedCourses")}
          value={assignedCoursesCount}
          icon={<AcademicCapIcon className="h-6 w-6" />}
        />
        <StatCard
          title={t("dashboard.completedCourses")}
          value={completedCourses}
          icon={<CheckBadgeIcon className="h-6 w-6" />}
          // Optionally, you can calculate change vs. last month if you want
        />
        <StatCard
          title={t("dashboard.badges")}
          value="7"
          icon={<ShieldCheckIcon className="h-6 w-6" />}
        />
        <StatCard
          title={t("dashboard.progress")}
          value={formatPercentage(overallProgress)}
          icon={<DocumentTextIcon className="h-6 w-6" />}
          // Optionally, you can calculate change vs. last month if you want
        />

        {/* Overall Quiz Performance */}
        <StatCard
          title={t("dashboard.averageQuizScore", "Avg Quiz Score")}
          value={(() => {
            const allQuizzes = learningProgress.flatMap(
              (c: any) => c.quizzes || []
            );
            if (allQuizzes.length === 0) return "N/A";
            const totalScore = allQuizzes.reduce(
              (sum: number, quiz: any) => sum + quiz.score,
              0
            );
            return formatScore(Math.round(totalScore / allQuizzes.length));
          })()}
          icon={<CheckBadgeIcon className="h-6 w-6" />}
        />

        {/* User Ranking - Only show for employees and department admins */}
        {(user?.role === "employee" || user?.role === "department_admin") &&
          (rankingLoading ? (
            <StatCard
              title={t("dashboard.yourRanking", "Your Ranking")}
              value="..."
              icon={<TrophyIcon className="h-6 w-6" />}
            />
          ) : rankingData?.data ? (
            <StatCard
              title={`${t("dashboard.yourRanking", "Your Ranking")} (of ${
                rankingData.data.totalEmployees
              })`}
              value={`#${rankingData.data.rank}`}
              icon={<TrophyIcon className="h-6 w-6" />}
            />
          ) : (
            <StatCard
              title={`${t("dashboard.yourRanking", "Your Ranking")}`}
              value="N/A"
              icon={<TrophyIcon className="h-6 w-6" />}
            />
          ))}
      </div>

      {/* Two-column layout for smaller components */}
      <div className="mt-6 sm:mt-8 grid gap-6 sm:gap-8 lg:grid-cols-2">
      </div>

      {/* Individual Course Performance Section */}
      <div className="mt-6 sm:mt-8">
        <Card animate>
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("dashboard.coursePerformance", "Course Performance")}
          </h2>
          {learningProgress.length > 0 ? (
            <div className="space-y-4">
              {learningProgress.map((course: any, index: number) => {
                // Calculate average quiz score for this course
                const quizScores = course.quizzes || [];
                const averageQuizScore =
                  quizScores.length > 0
                    ? Math.round(
                        quizScores.reduce(
                          (sum: number, quiz: any) => sum + quiz.score,
                          0
                        ) / quizScores.length
                      )
                    : 0;

                // Get course title from courses array
                const courseData = courses.find(
                  (c: any) => c._id === course.courseId
                );
                const courseTitle = courseData
                  ? getCourseTitle(courseData)
                  : `Course ${index + 1}`;

                return (
                  <div
                    key={course.courseId || index}
                    className="rounded-lg border border-gray-200 p-3 sm:p-4 dark:border-gray-700"
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between mb-3 gap-2">
                      <h3 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white break-words line-clamp-2">
                        {courseTitle}
                      </h3>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <span
                          className={`px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap ${
                            course.progress === 100
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                          }`}
                        >
                          {formatPercentage(course.progress)}{" "}
                        {course.progress === 100
                          ? t("courses.completed")
                          : t("courses.inProgress")}
                        </span>
                        {course.completedAt && (
                          <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap hidden sm:inline">
                            {t("courses.completedPrefix")}{" "}
                            {formatDate(course.completedAt)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                        <span>{t("common.progress")}</span>
                        <span>{formatPercentage(course.progress)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                        <div
                          className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: formatProgressBar(course.progress).width }}
                        ></div>
                      </div>
                    </div>

                    {/* Quiz Performance */}
                    {quizScores.length > 0 && (
                      <div className="mb-3">
                        <h4 className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t("courses.quizPerformance")}
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                          {quizScores.map((quiz: any, quizIndex: number) => (
                            <div
                              key={quiz.quizId || quizIndex}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded dark:bg-gray-800"
                            >
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                {t("courses.quiz")} {quizIndex + 1}
                              </span>
                              <div className="flex items-center space-x-2">
                                <span
                                  className={`px-2 py-1 rounded text-xs font-medium ${
                                    quiz.isPassed
                                      ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                      : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                  }`}
                                >
                                  {quiz.score}/5
                                </span>
                                <span
                                  className={`text-xs ${
                                    quiz.isPassed
                                      ? "text-green-600 dark:text-green-400"
                                      : "text-red-600 dark:text-red-400"
                                  }`}
                                >
                                  {quiz.isPassed ? t("courses.passed") : t("courses.failed")}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">{t("courses.averageScore")}:</span>{" "}
                          {averageQuizScore}/5
                        </div>
                      </div>
                    )}

                    {/* Tutorial Progress */}
                    {course.tutorials && course.tutorials.length > 0 && (
                      <div>
                        <h4 className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t("courses.tutorialProgress")}
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                          {course.tutorials.map(
                            (tutorial: any, tutorialIndex: number) => (
                              <div
                                key={tutorial.tutorialId || tutorialIndex}
                                className="flex items-center justify-between p-2 bg-gray-50 rounded dark:bg-gray-800"
                              >
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                  {t("courses.tutorial")} {tutorialIndex + 1}
                                </span>
                                <div className="flex items-center space-x-2">
                                  <span
                                    className={`px-2 py-1 rounded text-xs font-medium ${
                                      tutorial.isCompleted
                                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                        : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                    }`}
                                  >
                                    {tutorial.progress}%
                                  </span>
                                  <span
                                    className={`text-xs ${
                                      tutorial.isCompleted
                                        ? "text-green-600 dark:text-green-400"
                                        : "text-blue-600 dark:text-blue-400"
                                    }`}
                                  >
                                    {tutorial.isCompleted
                                      ? t("courses.completed")
                                      : t("courses.inProgress")}
                                  </span>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 dark:text-gray-500 mb-2">
                <AcademicCapIcon className="h-12 w-12 mx-auto" />
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                {user?.role === "department_admin"
                  ? t(
                      "dashboard.noQuizzesTaken",
                      "You haven't taken any quizzes yet"
                    )
                  : t(
                      "dashboard.noCoursesYet",
                      "No courses assigned yet. Check back later!"
                    )}
              </p>

              {/* Show request button for normal users (not department admins) */}
              {user?.role !== "department_admin" && !requestSent && (
                <div className="mt-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t(
                      "dashboard.requestCourseAssignmentDescription",
                      "Ask your administrator to assign courses to you"
                    )}
                  </p>
                  <Button
                    variant="primary"
                    onClick={handleRequestCourseAssignment}
                    disabled={requestCourseAssignmentMutation.isPending}
                    className="px-6 py-2"
                  >
                    {requestCourseAssignmentMutation.isPending ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        <span>
                          {t("dashboard.sendingRequest", "Sending request...")}
                        </span>
                      </div>
                    ) : (
                      t(
                        "dashboard.requestCourseAssignment",
                        "Request Course Assignment"
                      )
                    )}
                  </Button>
                </div>
              )}

              {/* Show success message after request is sent */}
              {user?.role !== "department_admin" && requestSent && (
                <div className="mt-6">
                  <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-lg">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {t("dashboard.requestSent", "Request sent successfully!")}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
